import { Tooltip } from "flowbite-react";
import { cloneDeep, findIndex, get, size } from "lodash";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import styled from "styled-components";
import allImgPaths from "../../assets";
import { useDebounce } from "../../hooks/useDebounce";
import useOutsideClick from "../../hooks/useOutsideClick";
import Checkbox from "./Checkbox";

interface Props {
  name: string;
  //control?: any;
  errors?: any;
  value: any;
  className?: string;
  trigger?: any;
  validMaxValue?: number;
  options: any[];
  showSearch?: boolean;
  isShowSelectedCount?: boolean;
  onChange: (opt: any) => void;
  required?: boolean;
}
const DropDownWithCheckBoxSearch = ({
  trigger,
  validMaxValue = 0,
  name,
  options,
  onChange,
  //control,
  errors,
  className,
  showSearch = true,
  value,
  isShowSelectedCount = true,
  required = true,
}: Props) => {
  const [show, setShow] = useState(false);
  const actionsRef = useRef(null);

  const [search, setSearch] = useState("");
  const debouncedSearch: string | number = useDebounce(search, 700);

  const [isSelectAll, setIsSelectAll] = useState(false);

  const [selectedOpt, setSelectedOpt] = useState<number[]>([]);

  const [latestOptions, setLatestOptions] = useState<any[]>([]);

  useEffect(() => {
    onChange(value);
  }, [value?.length]);

  /* console.log(
    "DropDownChkBox errors: ",
    errors,
    get(errors, `${name}.message`),
  ); */

  useEffect(() => {
    //console.log("=> value", value.length, " === ", options.length);

    setIsSelectAll(value.length !== 0 && value.length === options.length);
    setSelectedOpt(size(value) > 0 ? value : []);
  }, [value]);

  //console.log("selectedOpt: ", selectedOpt, " value: ", value);

  useEffect(() => {
    //console.log("options", options);
    const findFilterObj: any = options.filter((obj) =>
      JSON.stringify(obj)
        .toLowerCase()
        .includes(String(debouncedSearch).toLowerCase()),
    );
    //console.log("findFilterObj: ", findFilterObj);
    setLatestOptions(findFilterObj);
  }, [debouncedSearch, options]);

  const onSelect = (option: { name: string; id: number }) => {
    const _selectedOpt: number[] = cloneDeep(selectedOpt);
    //console.log("_selectedOpt ", _selectedOpt);

    const index = findIndex(selectedOpt, (o: any) => o === option.id);

    if (index >= 0) {
      _selectedOpt.splice(index, 1);
    } else {
      _selectedOpt.push(option.id);
    }

    if (validMaxValue !== 0) {
      if (_selectedOpt.length > validMaxValue) {
        _selectedOpt.pop();
        toast.error(`You can select up to ${validMaxValue} options only`);
        return;
      }
    }
    //console.log("## setIsSelectAll", _selectedOpt.length, options.length);
    //console.log("## setSelectedOpt", _selectedOpt);

    setIsSelectAll(_selectedOpt.length === options.length);
    setSelectedOpt(_selectedOpt);
    //onChange(_selectedOpt);
    onChange && onChange(_selectedOpt);
  };

  useOutsideClick(actionsRef, (): void => {
    setShow(false);
  });

  const onSelectAll = () => {
    let _selectedOpt: number[] = cloneDeep(selectedOpt);

    _selectedOpt = [];
    options.forEach((opt) => {
      _selectedOpt.push(opt.id);
    });
    setSelectedOpt(_selectedOpt);
    //onChange(_selectedOpt);
    onChange && onChange(_selectedOpt);
  };

  const onUnSelectAll = () => {
    setSelectedOpt([]);
    //onChange([]);
    onChange && onChange([]);
    setIsSelectAll(false);
  };

  const getSelectedOptLabel = () => {
    const selectedOptLabels: string[] = [];
    value.forEach((opt: any) => {
      if (value.includes(opt.id)) {
        selectedOptLabels.push(opt.name);
      }
    });
    return selectedOptLabels.join(", ");
  };

  return (
    <div className={className} ref={actionsRef}>
      <div
        className={`container ${get(errors, `${name}.message`) ? "error" : ""}`}
        onClick={() => setShow((prev) => !prev)}
      >
        <div className="text-sm select-none text-[#1B1C1E]">
          {trigger}
          {required && <span className="text-red-500 ml-1">*</span>}{" "}
        </div>
        <div className="flex">
          {options.length > 0 && (
            <>
              {isShowSelectedCount && size(selectedOpt) > 0 && (
                <div className="flex items-center justify-center h-5 w-5 bg-[#00a48b] text-white rounded-full p-1 mr-2">
                  <Tooltip
                    className="w-full"
                    content={getSelectedOptLabel()}
                    placement="top"
                  >
                    {size(selectedOpt)}
                  </Tooltip>
                </div>
              )}
            </>
          )}
          <img
            src={allImgPaths.dropdownIcon}
            alt="down-arrow"
            className={`h-3 w-3 transform transition-all duration-300 select-none ${show ? "rotate-180" : "rotate-0"} `}
          />
        </div>
      </div>
      {show && (
        <div className="menu-list">
          {showSearch && (
            <div className="w-full">
              <div className="relative">
                <input
                  className="w-full h-8 rounded-[5px] outline-none border-[#1c1c2c] focus:!border-[#1c1c2c] focus:!shadow-none border-[1px] outline-0"
                  type="text"
                  value={search}
                  placeholder="Search"
                  onChange={(event) => setSearch(event.target.value)}
                />
                <div className="absolute right-1 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-5 w-5 items-center justify-center flex">
                  <img
                    className="cursor-pointer"
                    src={search ? allImgPaths.cross : allImgPaths.search}
                    alt="search"
                    onClick={() => search && setSearch("")}
                  />
                </div>
              </div>
            </div>
          )}
          {options.length > 0 ? (
            <>
              <div
                className="menu flex gap-x-2 w-full cursor-pointer select-none"
                onClick={() => {
                  if (isSelectAll) {
                    setIsSelectAll(false);
                  } else {
                    setIsSelectAll(true);
                    onSelectAll();
                  }
                }}
              >
                <img
                  className="h-3 mt-1"
                  src={
                    isSelectAll ? allImgPaths.greenTick : allImgPaths.blacKTick
                  }
                />
                <span className="text-sm">Check All</span>
              </div>
              <div
                className="menu flex gap-x-2 w-full cursor-pointer select-none"
                onClick={() => onUnSelectAll()}
              >
                <img className="h-3 mt-1" src={allImgPaths.cross} />
                <span className="text-sm">Uncheck All</span>
              </div>
            </>
          ) : (
            <>No record found</>
          )}
          {latestOptions.map((opt, idx) => (
            <div
              className="menu flex gap-x-2 w-full cursor-pointer select-none"
              key={`lt1-${opt.id}-${idx}`}
            >
              {/* <input type="checkbox" checked={selectedOpt.includes(opt.id)} /> */}
              <Checkbox
                key={`chk1-${opt.name}-${idx}`}
                label={get(opt, "name", "N/A")}
                name={opt.id}
                checked={selectedOpt.includes(opt.id)}
                onChange={() => {
                  onSelect(opt);
                }}
              />
            </div>
          ))}
        </div>
      )}
      <div className="label-error-text text-red-500 text-xs">
        {get(errors, `${name}.message`) && (
          <span className="error-message">
            {get(errors, `${name}.message`, "")}
          </span>
        )}
      </div>
    </div>
  );
};

export default styled(DropDownWithCheckBoxSearch)`
  position: relative;
  height: 30px;
  width: 100%;
  .container {
    position: relative;
    height: 45px;
    border-radius: 12px;
    border: 1px solid grey;
    color: rgba(255, 255, 255, 0.8);
    min-width: auto;
    color: #333333;
    padding: 0 16px;
    border-radius: 10px;
    border: 1px solid rgba(15, 0, 64, 0.08);
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    gap: 10px;

    &.error {
      border-color: rgb(244, 67, 54) !important;
    }
  }
  .menu-list {
    margin: 0;
    z-index: 9999;
    position: absolute;
    right: 0;
    display: flex;
    align-items: center;
    flex-direction: column;
    width: auto;
    top: 45px;
    border-radius: 10px;
    background: #f9f5fd;
    padding: 16px;
    max-height: 200px;
    overflow-y: auto;
    gap: 6px;
    border: 1px solid lightgrey;
  }
`;
